import React, { useEffect, useRef, useState } from 'react';
import { ENDPOINTS, getAuthHeaders, handleAuthError } from './config';
import toast from 'react-hot-toast';

// Idempotency: one key per modal mount. A double-tap or network retry reuses
// the same key, so the backend returns the existing record instead of a dupe.
const newClientKey = () =>
  (window.crypto?.randomUUID?.() ?? `ck-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const defaultSettlementForm = {
  occurred_on: new Date().toISOString().split('T')[0],
  amount: '',
  currency: '',
  paid_by: '',
  paid_to: '',
  notes: '',
};

const CURRENCIES = [
  { code: 'CAD', flag: '🇨🇦' },
  { code: 'USD', flag: '🇺🇸' },
  { code: 'SGD', flag: '🇸🇬' },
  { code: 'EUR', flag: '🇪🇺' },
  { code: 'GBP', flag: '🇬🇧' },
  { code: 'MXN', flag: '🇲🇽' },
];

// ─── touch targets ───────────────────────────────────────────────────────────
// Same rule as ExpenseModal: a real 44x44 CSS-pixel hit area on every control
// (Apple HIG / WCAG 2.5.5 Target Size) and >=16px text in every field, because
// iOS Safari zooms the page when you focus a field smaller than that.
//
// `min-h-[44px]` with `inline-flex items-center` grows the box, not the type.
const FIELD_CLASS =
  'min-h-[44px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-base outline-none transition focus:border-slate-400';
const TEXTAREA_CLASS =
  'min-h-[100px] w-full rounded-3xl border border-slate-200 px-4 py-3 text-base outline-none transition focus:border-slate-400';
const CLOSE_BUTTON_CLASS =
  'inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-full border border-slate-200 px-4 text-sm text-slate-500 transition hover:border-slate-300 hover:bg-slate-50';
const SECONDARY_BUTTON_CLASS =
  'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50';
const PRIMARY_BUTTON_CLASS =
  'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60';

const SettlementModal = ({ editingRecord, initialForm, onClose, onSaved }) => {
  const [settlementForm, setSettlementForm] = useState(initialForm || defaultSettlementForm);
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Focus trap
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const getFocusable = () => Array.from(modal.querySelectorAll(focusableSelectors));

    // Focus first element
    const focusable = getFocusable();
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const elements = getFocusable();
      if (elements.length === 0) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleKeyDown);
    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const clientKeyRef = useRef(newClientKey());

  const saveSettlement = async (event) => {
    event.preventDefault();
    if (!settlementForm.currency || !settlementForm.paid_by || !settlementForm.paid_to) {
      toast.error('Choose the currency, payer, and receiver.');
      return;
    }
    if (settlementForm.paid_by === settlementForm.paid_to) {
      toast.error('Payer and receiver must be different.');
      return;
    }
    setSubmitting(true);
    const payload = {
      ...settlementForm,
      amount: Number(settlementForm.amount),
      client_key: editingRecord ? undefined : clientKeyRef.current,
    };
    try {
      const endpoint = editingRecord ? `${ENDPOINTS.EXPENSES_ENTRIES}/${editingRecord.id}` : ENDPOINTS.EXPENSES_SETTLEMENTS;
      const method = editingRecord ? 'PUT' : 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(editingRecord ? {
          occurred_on: payload.occurred_on,
          description: `${payload.paid_by === 'kate' ? 'Kate' : 'Navjeev'} paid ${payload.paid_to === 'kate' ? 'Kate' : 'Navjeev'}`,
          category: 'Payment',
          total_cost: payload.amount,
          currency: payload.currency,
          amount: payload.amount,
          paid_by: payload.paid_by,
          paid_to: payload.paid_to,
          notes: payload.notes,
        } : payload),
      });
      if (handleAuthError(response)) return;
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save settlement');
      }
      const result = await response.json();
      onSaved(result.dashboard);
      onClose();
      toast.success(editingRecord ? 'Settlement updated.' : 'Settlement recorded.');
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Could not save settlement.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4 backdrop-blur-sm">
      <form
        ref={modalRef}
        onSubmit={saveSettlement}
        className="w-full rounded-t-3xl sm:rounded-[32px] sm:max-w-xl border border-white/70 bg-white p-6 shadow-2xl max-h-[92dvh] overflow-y-auto"
      >
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              {editingRecord ? 'Edit settlement' : 'Record settlement'}
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">Settle up</h2>
            {!editingRecord ? (
              <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                Pick the actual currency you are settling in. Nothing is assumed.
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onClose} className={CLOSE_BUTTON_CLASS}>
            Close
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Date</span>
            <input type="date" className={FIELD_CLASS} value={settlementForm.occurred_on} onChange={(e) => setSettlementForm({ ...settlementForm, occurred_on: e.target.value })} required />
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Amount</span>
            <input type="number" min="0" step="0.01" className={FIELD_CLASS} value={settlementForm.amount} onChange={(e) => setSettlementForm({ ...settlementForm, amount: e.target.value })} placeholder="Amount" required />
          </label>
          <label className="grid gap-2">
            <span className="text-sm font-medium text-slate-600">Currency</span>
            <select className={FIELD_CLASS} value={settlementForm.currency} onChange={(e) => setSettlementForm({ ...settlementForm, currency: e.target.value })} required>
              <option value="" disabled>Choose currency</option>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-2">
              <span className="text-sm font-medium text-slate-600">Paid by</span>
              <select className={FIELD_CLASS} value={settlementForm.paid_by} onChange={(e) => setSettlementForm({ ...settlementForm, paid_by: e.target.value })} required>
                <option value="" disabled>Choose</option>
                <option value="kate">Kate paid</option>
                <option value="navjeev">Navjeev paid</option>
              </select>
            </label>
            <label className="grid gap-2">
              <span className="text-sm font-medium text-slate-600">Received by</span>
              <select className={FIELD_CLASS} value={settlementForm.paid_to} onChange={(e) => setSettlementForm({ ...settlementForm, paid_to: e.target.value })} required>
                <option value="" disabled>Choose</option>
                <option value="navjeev">Navjeev received</option>
                <option value="kate">Kate received</option>
              </select>
            </label>
          </div>
          <label className="grid gap-2 md:col-span-2">
            <span className="text-sm font-medium text-slate-600">Notes</span>
            <textarea className={TEXTAREA_CLASS} value={settlementForm.notes} onChange={(e) => setSettlementForm({ ...settlementForm, notes: e.target.value })} placeholder="Notes" />
          </label>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} className={PRIMARY_BUTTON_CLASS}>
            {submitting ? 'Saving...' : 'Save settlement'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettlementModal;
